(() => {
  const roots = document.querySelectorAll('[data-header-dropdown-cart]');
  if (!roots.length) return;

  const SYNC_DEBOUNCE_MS = 220;
  const cartUrl = () => (window.routes && window.routes.cart_url) || '/cart';
  const changeUrl = () => window.routes && window.routes.cart_change_url;
  const addUrl = () => window.routes && window.routes.cart_add_url;

  /** @type {{ items: Array, item_count: number } | null} */
  let cartCache = null;
  /** @type {Promise<{ items: Array, item_count: number }> | null} */
  let cartFetchPromise = null;
  /** @type {Map<string, number>} */
  const desiredQty = new Map();
  /** @type {Map<string, ReturnType<typeof setTimeout>>} */
  const syncTimers = new Map();
  /** @type {Map<string, Promise<void>>} */
  const syncChains = new Map();
  /** @type {Array<{ getVariantId: () => string, updateQtyDisplay: (qty: number) => void }>} */
  const steppers = [];

  const silentRenderOptions = { open: false };

  const getSectionsForCartRequest = () => {
    const ids = [];
    const seen = new Set();
    const pushFrom = (el) => {
      if (!el || typeof el.getSectionsToRender !== 'function') return;
      el.getSectionsToRender().forEach((s) => {
        if (s?.id && !seen.has(s.id)) {
          seen.add(s.id);
          ids.push(s.id);
        }
      });
    };
    pushFrom(document.querySelector('cart-drawer'));
    pushFrom(document.querySelector('cart-notification'));
    if (ids.length) return ids;
    return ['cart-drawer', 'cart-icon-bubble'];
  };

  const sumQtyForVariant = (items, variantId) => {
    if (!items?.length || variantId == null || variantId === '') return 0;
    const id = Number(variantId);
    return items.reduce((sum, line) => {
      if (Number(line.variant_id) !== id) return sum;
      return sum + (Number(line.quantity) || 0);
    }, 0);
  };

  const findLineForVariant = (items, variantId) => {
    if (!items?.length) return null;
    const id = Number(variantId);
    return items.find((line) => Number(line.variant_id) === id) || null;
  };

  const setCartCache = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return;
    cartCache = {
      items: cart.items,
      item_count: typeof cart.item_count === 'number' ? cart.item_count : cart.items.length,
    };
  };

  const fetchCart = (force = false) => {
    if (!force && cartCache) return Promise.resolve(cartCache);
    if (cartFetchPromise) return cartFetchPromise;

    cartFetchPromise = fetch(`${cartUrl()}.js`)
      .then((r) => {
        if (!r.ok) throw new Error('cart');
        return r.json();
      })
      .then((cart) => {
        setCartCache(cart);
        return cartCache;
      })
      .finally(() => {
        cartFetchPromise = null;
      });

    return cartFetchPromise;
  };

  const syncAllSteppersFromCache = () => {
    if (!cartCache) return;
    steppers.forEach((stepper) => {
      const vid = stepper.getVariantId();
      if (!vid) {
        stepper.updateQtyDisplay(0);
        return;
      }
      if (desiredQty.has(vid)) {
        stepper.updateQtyDisplay(desiredQty.get(vid));
        return;
      }
      stepper.updateQtyDisplay(sumQtyForVariant(cartCache.items, vid));
    });
  };

  const updateCartIconCount = (count) => {
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink) return;

    let bubble = cartLink.querySelector('.cart-count-bubble');
    const n = Math.max(0, Number(count) || 0);

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      cartLink.appendChild(bubble);
    }

    let visible = bubble.querySelector('[aria-hidden="true"]');
    if (!visible) {
      bubble.innerHTML = '';
      visible = document.createElement('span');
      visible.setAttribute('aria-hidden', 'true');
      bubble.appendChild(visible);
      const sr = document.createElement('span');
      sr.className = 'visually-hidden';
      bubble.appendChild(sr);
    }

    visible.textContent = n < 100 ? String(n) : '';
    const srEl = bubble.querySelector('.visually-hidden');
    if (srEl) srEl.textContent = String(n);
  };

  const renderCartSectionsFromResponse = (parsedState) => {
    if (!parsedState?.sections) return;

    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function' && parsedState.sections['cart-drawer']) {
      try {
        drawer.renderContents(parsedState, silentRenderOptions);
        const count =
          typeof parsedState.item_count === 'number'
            ? parsedState.item_count
            : Array.isArray(parsedState.items)
              ? parsedState.items.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0)
              : 0;
        drawer.classList.toggle('is-empty', count === 0);
      } catch (e) {
        console.error(e);
      }
    }

    if (parsedState.sections['cart-icon-bubble']) {
      const bubbleEl = document.getElementById('cart-icon-bubble');
      if (bubbleEl && drawer && typeof drawer.getSectionInnerHTML === 'function') {
        try {
          bubbleEl.innerHTML = drawer.getSectionInnerHTML(parsedState.sections['cart-icon-bubble']);
        } catch (e) {
          /* ignore bubble paint errors */
        }
      }
    }

    const notification = document.querySelector('cart-notification');
    if (
      notification &&
      typeof notification.renderContents === 'function' &&
      parsedState.key &&
      (parsedState.sections['cart-notification-product'] || parsedState.sections['cart-notification-button'])
    ) {
      try {
        notification.renderContents(parsedState);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const refreshCartSectionsInBackground = (cart) => {
    const sectionIds = getSectionsForCartRequest();
    if (!sectionIds.length) return;

    const params = new URLSearchParams();
    params.set('sections', sectionIds.join(','));
    const url = `${window.location.pathname}?${params.toString()}`;

    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error('sections');
        return r.json();
      })
      .then((sections) => {
        renderCartSectionsFromResponse({
          sections,
          item_count: cart?.item_count,
          items: cart?.items,
        });
      })
      .catch(() => {});
  };

  const publishCartUpdate = (cart, variantId) => {
    if (typeof publish === 'undefined' || typeof PUB_SUB_EVENTS === 'undefined') return;
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'header-dropdown-cart',
      cartData: cart,
      productVariantId: variantId,
    });
  };

  const persistDesiredQty = (variantId) => {
    const existing = syncChains.get(variantId) || Promise.resolve();
    const next = existing
      .catch(() => {})
      .then(async () => {
        const qtyToPersist = desiredQty.has(variantId)
          ? desiredQty.get(variantId)
          : sumQtyForVariant(cartCache?.items, variantId);

        if (typeof fetchConfig === 'undefined') return;

        let cart = cartCache;
        if (!cart) cart = await fetchCart();

        const line = findLineForVariant(cart?.items, variantId);
        const currentQty = sumQtyForVariant(cart?.items, variantId);

        if (qtyToPersist === currentQty && (qtyToPersist === 0 || line)) {
          if (desiredQty.get(variantId) === qtyToPersist) desiredQty.delete(variantId);
          return;
        }

        try {
          if (!line && qtyToPersist > 0) {
            if (!addUrl()) throw new Error('add-url');
            const config = fetchConfig('javascript');
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            config.body = JSON.stringify({
              items: [{ id: Number(variantId), quantity: qtyToPersist }],
            });
            const addRes = await fetch(addUrl(), config);
            const addJson = await addRes.json();
            if (addJson.status || addJson.errors) throw new Error(addJson.description || 'add-failed');

            const addedItems = Array.isArray(addJson.items)
              ? addJson.items
              : addJson.variant_id || addJson.id
                ? [addJson]
                : [];

            if (addedItems.length) {
              const baseItems = Array.isArray(cartCache?.items) ? [...cartCache.items] : [];
              addedItems.forEach((added) => {
                const idx = baseItems.findIndex(
                  (item) =>
                    item.key === added.key || Number(item.variant_id) === Number(added.variant_id || added.id)
                );
                if (idx >= 0) baseItems[idx] = { ...baseItems[idx], ...added };
                else baseItems.push(added);
              });
              const itemCount = baseItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
              setCartCache({ items: baseItems, item_count: itemCount });
              cart = cartCache;
            } else {
              cart = await fetchCart(true);
            }
          } else if (line) {
            if (!changeUrl()) throw new Error('change-url');
            const body = JSON.stringify({
              id: line.key,
              quantity: qtyToPersist,
            });
            const changeRes = await fetch(changeUrl(), { ...fetchConfig(), body });
            const text = await changeRes.text();
            const parsedState = JSON.parse(text);
            if (parsedState.errors) throw new Error(parsedState.errors);
            setCartCache(parsedState);
            cart = cartCache;
          } else {
            if (desiredQty.get(variantId) === qtyToPersist) desiredQty.delete(variantId);
            syncAllSteppersFromCache();
            return;
          }

          if (desiredQty.get(variantId) === qtyToPersist) desiredQty.delete(variantId);
          syncAllSteppersFromCache();
          updateCartIconCount(cart?.item_count || 0);

          publishCartUpdate(cart, variantId);
          refreshCartSectionsInBackground(cart);
        } catch (e) {
          console.error(e);
          desiredQty.delete(variantId);
          try {
            await fetchCart(true);
          } catch (_) {
            /* ignore */
          }
          syncAllSteppersFromCache();
          updateCartIconCount(cartCache?.item_count || 0);
        }
      });

    syncChains.set(variantId, next);
    return next;
  };

  const schedulePersist = (variantId) => {
    const prev = syncTimers.get(variantId);
    if (prev) clearTimeout(prev);
    syncTimers.set(
      variantId,
      setTimeout(() => {
        syncTimers.delete(variantId);
        persistDesiredQty(variantId);
      }, SYNC_DEBOUNCE_MS)
    );
  };

  const adjustVariantQty = (variantId, delta) => {
    if (!variantId) return;

    const base = desiredQty.has(variantId)
      ? desiredQty.get(variantId)
      : sumQtyForVariant(cartCache?.items, variantId);
    const next = Math.max(0, base + delta);
    desiredQty.set(variantId, next);

    steppers.forEach((stepper) => {
      if (String(stepper.getVariantId()) === String(variantId)) {
        stepper.updateQtyDisplay(next);
      }
    });

    if (cartCache) {
      const current = sumQtyForVariant(cartCache.items, variantId);
      const itemCount = Math.max(0, (cartCache.item_count || 0) + (next - current));
      updateCartIconCount(itemCount);
    }

    schedulePersist(variantId);
  };

  const initRoot = (root) => {
    const stepper = root.querySelector('[data-header-dropdown-cart-stepper]');
    const submitBtn = root.querySelector('[data-header-dropdown-cart-inc], [type="submit"]');
    const submitLabel = submitBtn?.querySelector('.visually-hidden');
    const addLabelEl = root.querySelector('[data-header-dropdown-cart-add-label]');
    const qtyEl = root.querySelector('[data-header-dropdown-cart-qty]');
    const decBtn = root.querySelector('[data-header-dropdown-cart-dec]');
    const getVariantId = () => {
      const input = root.querySelector('input[name="id"]');
      return input?.value || '';
    };

    const updateQtyDisplay = (qty) => {
      const n = Number(qty) || 0;
      if (qtyEl) {
        qtyEl.textContent = n > 0 ? String(n) : '';
        qtyEl.setAttribute('aria-hidden', n > 0 ? 'false' : 'true');
      }
      if (stepper) {
        stepper.classList.toggle('product-story-sticky__stepper--has-line-qty', n > 0);
      }
      if (decBtn) {
        const soldOut = decBtn.hasAttribute('data-header-sold-out');
        decBtn.disabled = soldOut || n < 1;
      }
      if (submitBtn) {
        const soldOut = submitBtn.hasAttribute('data-header-sold-out');
        if (!soldOut) submitBtn.disabled = false;
        const base = submitBtn.getAttribute('data-add-to-cart-label') || submitLabel?.textContent?.trim() || '';
        const increaseAria = submitBtn.getAttribute('data-aria-label-increase') || '';
        if (n > 0 && increaseAria) {
          submitBtn.setAttribute('aria-label', increaseAria);
        } else {
          submitBtn.setAttribute('aria-label', base);
        }
      }
      if (addLabelEl && submitBtn) {
        addLabelEl.textContent =
          submitBtn.getAttribute('data-add-to-cart-label') || submitLabel?.textContent?.trim() || '';
      }
    };

    steppers.push({ getVariantId, updateQtyDisplay });

    if (decBtn) {
      decBtn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (decBtn.disabled || decBtn.hasAttribute('data-header-sold-out')) return;
        adjustVariantQty(getVariantId(), -1);
      });
    }

    if (submitBtn) {
      submitBtn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (submitBtn.disabled || submitBtn.hasAttribute('data-header-sold-out')) return;
        adjustVariantQty(getVariantId(), 1);
      });
    }

    root.addEventListener('click', (event) => event.stopPropagation());
  };

  roots.forEach(initRoot);

  fetchCart()
    .then(() => {
      syncAllSteppersFromCache();
      updateCartIconCount(cartCache?.item_count || 0);
    })
    .catch(() => {
      steppers.forEach((stepper) => stepper.updateQtyDisplay(0));
    });

  if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source === 'header-dropdown-cart') return;

      if (event?.cartData && Array.isArray(event.cartData.items)) {
        setCartCache(event.cartData);
        syncAllSteppersFromCache();
        updateCartIconCount(cartCache?.item_count || 0);
        return;
      }

      fetchCart(true)
        .then(() => {
          syncAllSteppersFromCache();
          updateCartIconCount(cartCache?.item_count || 0);
        })
        .catch(() => {});
    });
  }
})();
